import { TicketStatus } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys } from 'noddi-async';
import {
  ApiErrorMessageInternal,
  NavioPage,
  NoddiNotificationBadge,
  NoddiTable,
  TabItem,
  useNoddiSortOrderParams
} from 'noddi-ui-web';
import { useMemo, useState } from 'react';
import { useAuthContext } from '../../contexts/useAuthContext';

import { useMyMentionsUnreadCount } from './hooks/useMyMentionsUnreadCount';
import { useMyTicketsCount } from './hooks/useMyTicketsCount';
import { useTicketListQuickActions } from './hooks/useTicketListQuickActions';
import { useTicketsQueryParams } from './hooks/useTicketsQueryParams';
import { useTicketStatusTabCounts } from './hooks/useTicketStatusTabCounts';
import { MentionsTabPanel } from './MentionsTabPanel';
import { getTicketEventSource, TicketApp } from './routes';
import { TicketCreateSheet } from './TicketCreateSheet';
import { TicketDetailDrawer } from './TicketDetailDrawer';
import { getTicketListColumns } from './TicketListColumns';

type Props = {
  app: TicketApp;
  /** Scope the list + ticket creation to departments owned by these service orgs (facility app). */
  serviceOrganizationIds?: number[];
  /** Scope the list + ticket creation to these service department IDs (partner app). */
  serviceDepartmentIds?: number[];
};

type TabValue = 'mine' | 'open' | 'snoozed' | 'resolved' | 'archived' | 'mentions';

const ACTIVE_STATUSES: TicketStatus[] = [TicketStatus.OPEN, TicketStatus.SNOOZED];

const STATUS_TABS: Record<Exclude<TabValue, 'mine' | 'mentions'>, TicketStatus[]> = {
  open: [TicketStatus.OPEN],
  snoozed: [TicketStatus.SNOOZED],
  resolved: [TicketStatus.RESOLVED],
  archived: [TicketStatus.ARCHIVED]
};

export const TicketList = ({ app, serviceOrganizationIds, serviceDepartmentIds }: Props) => {
  const columns = useMemo(() => getTicketListColumns(app), [app]);
  const { pagination, setPagination, tab, setTab } = useTicketsQueryParams();
  const { order, onOrderChange } = useNoddiSortOrderParams({ columns });
  const { currentUserGroupId } = useAuthContext();
  const { actions: quickActions, dialogs: quickActionDialogs } = useTicketListQuickActions();

  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);

  const activeTab: TabValue = tab === 'mine' && currentUserGroupId ? 'mine' : tab;
  const safeTab: TabValue = activeTab === 'mine' && !currentUserGroupId ? 'open' : activeTab;
  const isMentionsTab = safeTab === 'mentions';

  const statuses = safeTab === 'mine' || isMentionsTab ? ACTIVE_STATUSES : (STATUS_TABS[safeTab] ?? STATUS_TABS.open);
  const assigneeIds = safeTab === 'mine' && currentUserGroupId ? [currentUserGroupId] : undefined;

  const { isLoading: isMyTicketsCountLoading, count: myTicketsCount } = useMyTicketsCount({
    serviceOrganizationIds,
    serviceDepartmentIds
  });

  const statusTabCounts = useTicketStatusTabCounts({
    serviceOrganizationIds,
    serviceDepartmentIds
  });

  const { isLoading: isMentionsCountLoading, count: mentionsCount } = useMyMentionsUnreadCount();

  const tabItems: TabItem<TabValue>[] = [
    {
      label: 'My tickets',
      value: 'mine',
      node: null,
      icon: 'User',
      hide: !currentUserGroupId,
      count: isMyTicketsCountLoading ? undefined : myTicketsCount
    },
    { label: 'All open', value: 'open', node: null, icon: 'Circle', count: statusTabCounts.open.count },
    { label: 'Snoozed', value: 'snoozed', node: null, icon: 'Clock', count: statusTabCounts.snoozed.count },
    { label: 'Resolved', value: 'resolved', node: null, icon: 'CircleCheck', count: statusTabCounts.resolved.count },
    { label: 'Archived', value: 'archived', node: null, icon: 'Archive', count: statusTabCounts.archived.count },
    {
      label: 'Mentions',
      value: 'mentions',
      node: null,
      icon: 'MessageSquare',
      badge: <NoddiNotificationBadge isLoading={isMentionsCountLoading} numberOfNotifications={mentionsCount} />
    }
  ];

  const {
    data: tickets,
    isPending,
    isFetching,
    error: ticketsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedTickets,
    input: {
      ...pagination,
      order,
      statuses,
      assigneeIds,
      serviceOrganizationIds,
      serviceDepartmentIds
    },
    queryConfig: { placeholderData: keepPreviousData, enabled: !isMentionsTab }
  });

  const listing = (
    <>
      {isMentionsTab ? (
        <MentionsTabPanel app={app} />
      ) : (
        <>
          {ticketsError ? <ApiErrorMessageInternal error={ticketsError} /> : null}

          <NoddiTable
            columns={columns}
            data={tickets?.results}
            pagination={pagination}
            setPagination={setPagination}
            totalCount={tickets?.count}
            isPending={isPending}
            isFetching={isFetching}
            searchPlaceholder='Search title or description...'
            ordering={order}
            onOrderChange={onOrderChange}
            getRowId={(row) => row.id}
            onRowClick={(row) => setSelectedTicketId(row.id)}
            actions={quickActions}
            emptyStateText='No tickets here.'
          />
        </>
      )}
    </>
  );

  return (
    <>
      <NavioPage
        title='Tickets'
        variant='secondary'
        description='Operational follow-ups tied to customers, cars, bookings, and storage.'
        primaryAction={{ label: 'New ticket', icon: 'Plus', onClick: () => setCreateOpen(true) }}
        // Every tab filters the same list; only the selected panel renders it.
        tabs={tabItems.map((tab) => ({ ...tab, node: listing }))}
        defaultTab='open'
        tabValue={safeTab}
        onTabChange={setTab}
      />
      <TicketCreateSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        source={getTicketEventSource(app)}
        serviceOrganizationIds={serviceOrganizationIds}
        serviceDepartmentIds={serviceDepartmentIds}
      />
      <TicketDetailDrawer
        ticketId={selectedTicketId}
        onClose={() => setSelectedTicketId(null)}
        app={app}
        serviceDepartmentIds={serviceDepartmentIds}
      />
      {quickActionDialogs}
    </>
  );
};
